import React, { useEffect, useState } from "react";
import Shell from "@/components/Shell";
import BillingPanel from "@/components/BillingPanel";
import api, { formatError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { KeyRound, ExternalLink, Trash2, Save, Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function Settings() {
  const { refresh } = useAuth();
  const [status, setStatus] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/api/settings/anthropic-key");
      setStatus(data);
    } catch (e) {}
  };

  useEffect(() => {
    load();
    // restore any previously revealed key into session memory so iframes inherit
    const cached = localStorage.getItem("vbs_anthropic_key");
    if (cached) {
      // Don't display the cached key — this is purely for iframe inheritance.
    }
  }, []);

  const saveKey = async (e) => {
    e.preventDefault();
    if (!apiKey.trim().startsWith("sk-ant-")) {
      return toast.error("API key must start with sk-ant-");
    }
    setBusy(true);
    try {
      await api.post("/api/settings/anthropic-key", { api_key: apiKey.trim() });
      // Store raw key in localStorage for direct-browser iframe usage
      localStorage.setItem("vbs_anthropic_key", apiKey.trim());
      toast.success("API key saved and verified");
      setApiKey("");
      await load();
      await refresh();
    } catch (err) {
      const raw = formatError(err.response?.data?.detail) || err.message || "Could not save key";
      // Try to extract a clean message if Anthropic returned a JSON error blob
      let msg = raw;
      try {
        const m = raw.match(/\{[\s\S]*\}/);
        if (m) {
          const p = JSON.parse(m[0]);
          const t = p?.error?.type || p?.type;
          const detail = p?.error?.message || p?.message;
          if (t && detail) msg = `${t}: ${detail}`;
          else if (detail) msg = detail;
        }
      } catch (_) {}
      if (msg.length > 160) msg = "Anthropic rejected this key. Double-check and try again.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your Anthropic API key? Tools will stop working until you set a new one.")) return;
    setBusy(true);
    try {
      await api.delete("/api/settings/anthropic-key");
      localStorage.removeItem("vbs_anthropic_key");
      toast.success("API key removed");
      setApiKey("");
      await load();
      await refresh();
    } catch (err) {
      toast.error("Could not remove key");
    } finally {
      setBusy(false);
    }
  };

  // Sync the stored key to localStorage when we reload the page (iframes need it)
  const syncFromServer = async () => {
    try {
      const { data } = await api.get("/api/settings/anthropic-key/reveal");
      if (data?.api_key) {
        localStorage.setItem("vbs_anthropic_key", data.api_key);
        toast.success("Key synced to this browser");
      }
    } catch (e) {
      toast.error("No key to sync");
    }
  };

  return (
    <Shell>
      <div className="vbs-page-eye">SETTINGS</div>
      <h1 className="vbs-page-h">
        Your <em>Anthropic</em> Key
      </h1>
      <p className="vbs-page-p">
        Every tool in the Viral Book Suite runs on <strong style={{ color: "var(--bright)" }}>Claude Sonnet 4.5</strong> via your own Anthropic API key. Your key is encrypted at rest and never shared. Get a key at{" "}
        <a href="https://console.anthropic.com/settings/keys" target="_blank" rel="noreferrer">
          console.anthropic.com <ExternalLink size={12} style={{ display: "inline" }} />
        </a>
        .
      </p>

      <div className="vbs-card" style={{ padding: 26 }}>
        {status?.has_anthropic_key ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <CheckCircle2 size={22} style={{ color: "#6dd4a0" }} />
            <div>
              <div style={{ fontWeight: 600, color: "var(--bright)" }} data-testid="key-status-connected">
                Anthropic key connected · Claude Sonnet 4.5 ready
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--muted)" }}>
                {status.masked}
              </div>
            </div>
            <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
              <button className="vbs-btn vbs-btn-ghost" onClick={syncFromServer} data-testid="sync-key-btn">
                Sync to browser
              </button>
              <button className="vbs-btn vbs-btn-coral" onClick={remove} disabled={busy} data-testid="remove-key-btn">
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        ) : (
          <div style={{ color: "var(--mid)", marginBottom: 18, fontSize: 14 }} data-testid="key-status-not-set">
            No key set. Paste your Anthropic API key below to activate the suite.
          </div>
        )}

        <form onSubmit={saveKey}>
          <label>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--gold-dim)",
                letterSpacing: 1.5,
                marginBottom: 6,
                textTransform: "uppercase",
              }}
            >
              {status?.has_anthropic_key ? "Replace API Key" : "Anthropic API Key"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                data-testid="api-key-input"
                className="vbs-input"
                type={showKey ? "text" : "password"}
                placeholder="sk-ant-api03-..."
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                required
                style={{ flex: 1, fontFamily: "var(--font-mono)" }}
              />
              <button
                type="button"
                className="vbs-btn vbs-btn-ghost"
                onClick={() => setShowKey((v) => !v)}
                data-testid="toggle-show-key"
                style={{ padding: "0 14px" }}
              >
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>
          <button
            type="submit"
            className="vbs-btn vbs-btn-gold"
            style={{ marginTop: 18 }}
            disabled={busy}
            data-testid="save-key-btn"
          >
            {busy ? "Verifying…" : "Save & Verify"}
            <Save size={14} />
          </button>
        </form>
      </div>

      <div className="vbs-card" style={{ padding: 22, marginTop: 18 }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 12,
          }}
        >
          <KeyRound size={22} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13.5, color: "var(--mid)", lineHeight: 1.7 }}>
            <strong style={{ color: "var(--bright)" }}>How this works.</strong> Your
            key is AES-encrypted (Fernet) before being stored in our database. When a
            tool makes a Claude call, your browser sends requests directly to Anthropic
            with your key — we don't see your prompts. You control spend and rate-limits
            on your own Anthropic account.
          </div>
        </div>
      </div>

      <BillingPanel />

      {/* ─────── OPENAI KEY (for ChatGPT Images — book cover rendering) ─────── */}
      <OpenAIKeySection />
    </Shell>
  );
}

// =============================================================================
// OpenAI Key Section — powers ChatGPT Images (gpt-image-1) on Stage 05 of
// Viral Book Studio and Viral Book Cover Studio. Persisted server-side
// (encrypted at rest) so the key survives browser data clears and syncs across
// devices — mirrors the Anthropic key pattern.
// =============================================================================
function OpenAIKeySection() {
  const [openaiKey, setOpenaiKey] = React.useState("");
  const [showKey, setShowKey] = React.useState(false);
  const [savedMasked, setSavedMasked] = React.useState("");
  const [hasKey, setHasKey] = React.useState(false);
  const [saving, setSaving] = React.useState(false);

  // Load status from backend on mount + sync to localStorage for iframe tools
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/settings/openai-key");
        if (cancelled) return;
        if (data.has_openai_key) {
          setHasKey(true);
          setSavedMasked(data.masked || "sk-...");
          // Sync to localStorage for iframe tools that need direct api.openai.com access
          try {
            const { data: reveal } = await api.get("/api/settings/openai-key/reveal");
            if (!cancelled && reveal && reveal.api_key) {
              localStorage.setItem("vbs_openai_key", reveal.api_key);
            }
          } catch (revealErr) {}
        }
      } catch (statusErr) {
        // Fallback: try localStorage (for users on stale frontend)
        try {
          const k = localStorage.getItem("vbs_openai_key");
          if (!cancelled && k) {
            setHasKey(true);
            setSavedMasked("sk-...".concat(k.slice(-4)));
          }
        } catch (e) {}
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    const inputEl = typeof document !== "undefined" ? document.querySelector('[data-testid="openai-key-input"]') : null;
    const raw = (openaiKey || (inputEl && inputEl.value) || "").trim();
    if (!raw) return toast.error("Please paste your OpenAI API key first");
    if (!raw.startsWith("sk-")) return toast.error("OpenAI keys start with sk-");
    setSaving(true);
    try {
      // Save server-side (encrypted at rest, survives browser data clears)
      await api.post("/api/settings/openai-key", { api_key: raw });
      // Mirror to localStorage so open iframes pick it up immediately (best-effort)
      try {
        localStorage.setItem("vbs_openai_key", raw);
        window.dispatchEvent(new StorageEvent("storage", { key: "vbs_openai_key", newValue: raw }));
      } catch (storageErr) {}
      setHasKey(true);
      setSavedMasked("sk-...".concat(raw.slice(-4)));
      setOpenaiKey("");
      if (inputEl) inputEl.value = "";
      toast.success("OpenAI key saved");
    } catch (err) {
      const msg = err?.response?.data?.detail || err?.message || "Server error";
      toast.error("Could not save key: " + msg);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Remove your OpenAI API key?")) return;
    try {
      await api.delete("/api/settings/openai-key");
    } catch (err) {
      // Continue even if server delete fails (e.g. offline) — remove locally
    }
    try { localStorage.removeItem("vbs_openai_key"); } catch (e) {}
    setHasKey(false);
    setSavedMasked("");
    toast.success("OpenAI key removed");
  };

  return (
    <div style={{ marginTop: 34 }}>
      <div className="vbs-page-eye">CHATGPT IMAGES</div>
      <h1 className="vbs-page-h" style={{ marginBottom: 8 }}>
        Your <em>OpenAI</em> Key
      </h1>
      <p className="vbs-page-p" style={{ marginBottom: 20 }}>
        Adding your <strong style={{ color: "var(--bright)" }}>OpenAI API key</strong> unlocks the "Generate Image" button on every front cover, back cover and audiobook cover prompt in <strong>Viral Book Studio → Stage 05</strong> and <strong>Viral Book Cover Studio</strong>. Images are rendered with <strong>ChatGPT Images (gpt-image-1)</strong> and billed directly to your own OpenAI account. Get a key at{" "}
        <a href="https://platform.openai.com/api-keys" target="_blank" rel="noreferrer">
          platform.openai.com <ExternalLink size={12} style={{ display: "inline" }} />
        </a>
        .
      </p>

      <div className="vbs-card" style={{ padding: 26 }}>
        {savedMasked ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24, flexWrap: "wrap" }}>
            <CheckCircle2 size={22} style={{ color: "#6dd4a0" }} />
            <div>
              <div style={{ fontWeight: 600, color: "var(--bright)" }} data-testid="openai-key-status-connected">
                OpenAI key connected · ChatGPT Images ready
              </div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: 12, color: "var(--muted)" }}>
                {savedMasked}
              </div>
            </div>
            <div style={{ marginLeft: "auto" }}>
              <button className="vbs-btn vbs-btn-coral" onClick={remove} data-testid="remove-openai-key-btn">
                <Trash2 size={14} /> Remove
              </button>
            </div>
          </div>
        ) : (
          <div style={{ color: "var(--mid)", marginBottom: 18, fontSize: 14 }} data-testid="openai-key-status-not-set">
            No key set. Paste your OpenAI API key below to render cover images with ChatGPT Images.
          </div>
        )}

        <form onSubmit={save}>
          <label>
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                color: "var(--gold-dim)",
                letterSpacing: 1.5,
                marginBottom: 6,
                textTransform: "uppercase",
              }}
            >
              {savedMasked ? "Replace OpenAI Key" : "OpenAI API Key"}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                data-testid="openai-key-input"
                className="vbs-input"
                type={showKey ? "text" : "password"}
                placeholder="sk-..."
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                required
                style={{ flex: 1, fontFamily: "var(--font-mono)" }}
              />
              <button
                type="button"
                className="vbs-btn vbs-btn-ghost"
                onClick={() => setShowKey((v) => !v)}
                data-testid="toggle-show-openai-key"
                style={{ padding: "0 14px" }}
              >
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </label>
          <button
            type="submit"
            onClick={save}
            className="vbs-btn vbs-btn-gold"
            style={{ marginTop: 18 }}
            disabled={saving}
            data-testid="save-openai-key-btn"
          >
            {saving ? "Saving…" : "Save Key"}
            <Save size={14} />
          </button>
        </form>
      </div>

      <div className="vbs-card" style={{ padding: 22, marginTop: 18 }}>
        <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
          <KeyRound size={22} style={{ color: "var(--gold)", flexShrink: 0, marginTop: 2 }} />
          <div style={{ fontSize: 13.5, color: "var(--mid)", lineHeight: 1.7 }}>
            <strong style={{ color: "var(--bright)" }}>Encrypted server storage.</strong> Your OpenAI key is stored on our servers <em>encrypted at rest</em> (same protection as your Anthropic key) so it survives clearing browser data and syncs across every device you sign in on. It's transmitted only to <code>api.openai.com</code> when you click "Generate Image" on a cover prompt. You control your own OpenAI spend and rate-limits.
          </div>
        </div>
      </div>
    </div>
  );
}
